import { functions } from "@/main";
import { recallAdminRead, rememberAdminRead } from "@/utils/AdminReadCache";
import { SharedEmailAddressCleanUp, SharedUsersList, UserSearchResult } from "../models/SharedUsers";

/** Swap to `searchUsersForAdministrationDevelopment` **locally only** to drive the twin; never committed. */
const SEARCH_USERS_CALLABLE = "searchUsersForAdministration";
/** As above: `listSharedUsersForAdministrationDevelopment` locally only, never committed. */
const LIST_SHARED_USERS_CALLABLE = "listSharedUsersForAdministration";
/** As above: `cleanUpSharedEmailAddressForAdministrationDevelopment` locally only, never committed. */
const CLEAN_UP_SHARED_EMAIL_ADDRESS_CALLABLE = "cleanUpSharedEmailAddressForAdministration";

/**
 * Users across every client whose email address, nickname, staff number or name contains the search
 * (Benutzer suchen). Server-checked (`MANAGE_CLIENTS`) like every IRES Admin Client read; the email
 * address is returned on purpose (Taimoor, 2026-11-03).
 *
 * @param query - three characters or more.
 * @returns at most 100 rows and the number of matches. Rejects when Cloud refuses or the call fails.
 */
export async function searchUsersForAdministration(query: string): Promise<UserSearchResult> {
  const callable = functions.httpsCallable(SEARCH_USERS_CALLABLE);
  const result = await callable({ query });
  const data = result.data as Partial<UserSearchResult> | null;
  const users = Array.isArray(data?.users) ? data?.users ?? [] : [];
  const answer = { users, total: typeof data?.total === "number" ? data.total : users.length };
  rememberAdminRead(SEARCH_USERS_CALLABLE, query, answer);
  return answer;
}

/** The last answer of {@link searchUsersForAdministration} for this search in this tab, or null. */
export function cachedUserSearch(query: string): UserSearchResult | null {
  return recallAdminRead(SEARCH_USERS_CALLABLE, query);
}

/**
 * Both kinds of shared users (Geteilte Benutzer): every "E-Mail-Adresse teilen" entry with its users'
 * states, and every shared user of a group.
 *
 * @returns the two lists. Rejects when Cloud refuses or the call fails.
 */
export async function listSharedUsersForAdministration(): Promise<SharedUsersList> {
  const callable = functions.httpsCallable(LIST_SHARED_USERS_CALLABLE);
  const result = await callable({});
  const data = result.data as Partial<SharedUsersList> | null;
  const list = {
    emailAddresses: Array.isArray(data?.emailAddresses) ? data?.emailAddresses ?? [] : [],
    groupUsers: Array.isArray(data?.groupUsers) ? data?.groupUsers ?? [] : [],
  };
  rememberAdminRead(LIST_SHARED_USERS_CALLABLE, null, list);
  return list;
}

/** The last answer of {@link listSharedUsersForAdministration} in this tab, or null. */
export function cachedSharedUsers(): SharedUsersList | null {
  return recallAdminRead(LIST_SHARED_USERS_CALLABLE, null);
}

/**
 * Bereinigen: takes every user who no longer carries the entry's address out of it, and deletes the
 * entry when nobody is left. IRES Cloud re-reads the entry and its users, and records a Verlauf row in
 * each client that lost a user.
 *
 * @param sharedEmailAddressId - the entry.
 * @returns what changed. Rejects when Cloud refuses (`details.reason`, e.g. `SHARED_LOGIN`).
 */
export async function cleanUpSharedEmailAddress(sharedEmailAddressId: string): Promise<SharedEmailAddressCleanUp> {
  const callable = functions.httpsCallable(CLEAN_UP_SHARED_EMAIL_ADDRESS_CALLABLE);
  const result = await callable({ sharedEmailAddressId });
  const data = result.data as Partial<SharedEmailAddressCleanUp> | null;
  return {
    removed: typeof data?.removed === "number" ? data.removed : 0,
    deleted: data?.deleted === true,
    entry: data?.entry ?? null,
  };
}
